import { useAddressBookIndexContext } from "../providers";
import { useAppContext } from "../../../AppProvider";
import { useTypes } from "../../../utilities/types";
import {
  faArrowDownShortWide,
  faArrowUpShortWide,
  faPlus,
} from "../../../utilities/fontawesomeIcons";

/**
 * Provides all configuration props for the AddressBook List Page,
 * including filters, pagination, creation modal, mass actions, and UI texts.
 *
 * This hook centralizes logic and keeps the List view fully declarative.
 */
export const useListProps = () => {
  const { service, ownerId, myAddress, billing, setModalConfig } =
    useAddressBookIndexContext();

  const { dictionary, accessTokenPayload } = useAppContext();
  const { confirmationBoolOptions } = useTypes();

  const userIsOwner = accessTokenPayload.owner === ownerId;

  return {
    /** API endpoint path used by the ListView component */
    path: `/${service}-service/address-book`,

    /** Banner information (currently disabled) */
    banner: { enabled: false },

    /** Enable pagination */
    pagination: { enabled: true },

    /** Title of the page */
    title: userIsOwner
      ? dictionary.contacts.destinations_address_book
      : undefined,

    /** Create button configuration */
    create: {
      enabled: true,
      action: () =>
        setModalConfig({
          isOpen: true,
          action: "create",
          size: "md",
          title: dictionary.actions.add,
        }),
      title: dictionary.actions.add,
      icon: faPlus,
    },

    sorting: [
      {
        orderBy: "asc",
        column: "contactName",
        icon: faArrowUpShortWide,
        label: `${dictionary.registries.name} ${dictionary.words.and} ${dictionary.users.surname}`,
        isDefault: true,
      },
      {
        orderBy: "desc",
        column: "contactName",
        icon: faArrowDownShortWide,
        label: `${dictionary.registries.name} ${dictionary.words.and} ${dictionary.users.surname}`,
        isDefault: false,
      },
      {
        orderBy: "asc",
        column: "name",
        icon: faArrowUpShortWide,
        label: dictionary.registries.business_name,
        isDefault: false,
      },
      {
        orderBy: "desc",
        column: "name",
        icon: faArrowDownShortWide,
        label: dictionary.registries.business_name,
        isDefault: false,
      },
    ],

    /** Enable checkbox selection for massive actions */
    selectRowsEnabled: { enabled: true },

    /** Filters applied to the list */
    filters: {
      name: "destination-address-book",
      preserve: true,
      list: [
        {
          fieldName: "myAddress",
          label: dictionary.words.public,
          type: "select",
          primary: true,
          options: confirmationBoolOptions,
          col: 2,
          value: {
            value: myAddress,
            label: myAddress ? dictionary.words.yes : dictionary.words.no,
            isBool: true,
          },
          hidden: true,
        },
        {
          fieldName: "ownerId",
          label: dictionary.words.public,
          type: "text",
          primary: true,
          col: 2,
          value: ownerId,
          hidden: true,
        },
        {
          fieldName: "priv",
          label: dictionary.words.private,
          type: "select",
          primary: true,
          options: confirmationBoolOptions,
          col: 2,
        },
        {
          fieldName: "contactName",
          label: `${dictionary.registries.name} ${dictionary.words.and} ${dictionary.users.surname}`,
          type: "text",
          primary: true,
          placeholder: "Mario Rossi",
          col: 3,
        },
        {
          fieldName: "name",
          label: `${dictionary.registries.business_name}`,
          type: "text",
          primary: true,
          placeholder: "Allabox",
          col: 3,
        },
        {
          fieldName: "externalReference",
          label: `${dictionary.references.external_reference}`,
          type: "text",
          primary: true,
          placeholder: "Allabox",
          col: 2,
        },
        {
          fieldName: "vatNumber",
          label: `${dictionary.registries.vat_number}`,
          type: "text",
          primary: true,
          placeholder: "Allabox",
          col: 2,
          hidden: billing?.enabled === false,
        },
      ],
    },

    /** Bulk (massive) actions applied to selected rows */
    massiveActions: [
      {
        label: dictionary.actions.delete_selected,
        className: "button-1 button-1-danger",
        onClick: (selectedRows: { id: string }[]) =>
          setModalConfig({
            isOpen: true,
            size: "sm",
            action: "delete",
            title: dictionary.actions.delete_selected,
            data: selectedRows.map((row) => row.id),
          }),
      },
    ],
  };
};
